<template>
  <button
    :class="[
      'playtime-button',
      { 'playtime-button--today': isTodayPerformance },
      { 'playtime-button--disabled': isDisabled },
    ]"
    :disabled="isDisabled"
    :title="disabledReason"
    @click="handleBookingClick"
  >
    <span class="playtime" v-if="dateDisplay">
      {{ dateDisplay }}
    </span>
    <span class="playtime" v-if="timeDisplay">
      {{ timeDisplay }}
    </span>
    <template v-if="hasAttributes">
      <span class="attributes">
        {{ attributes.map((attribute) => attribute.title || attribute).join(", ") }}
      </span>
      <span v-if="originalReleases" class="attributes">
        {{ originalReleases }}
      </span>
    </template>
  </button>
</template>

<script setup>
import { computed } from "vue";
import { useDateHelpers } from "../composables/useDateHelpers.js";
import { useScheduleStore } from "../stores/scheduleStore.js";

const props = defineProps({
  performance: {
    type: Object,
    required: true,
  },
  showDate: {
    type: Boolean,
    default: false,
  },
  movie: {
    type: Object,
    default: null,
  },
});

// Composables
const { formatDateTitle, unixDateTimeToTime, isToday } = useDateHelpers();
const scheduleStore = useScheduleStore();

const handleBookingClick = () => {
  const perf = props.performance;
  if (!perf.bookingLink) return;

  const bookingLink = perf.bookInline ? `/booking/${perf.performanceID}` : perf.bookingLink;
  const target = perf.bookInline ? "_self" : "_blank";
  const filmNumber = props.movie?.detailId || "1000";
  const location = scheduleStore.cityId || null;

  const event = new CustomEvent("openBooking", {
    detail: {
      bookingLink,
      target,
      filmNumber,
      location,
    },
    bubbles: true,
  });

  document.dispatchEvent(event);
};

const isTodayPerformance = computed(() => {
  return props.performance.date ? isToday(props.performance.date) : false;
});

const dateDisplay = computed(() => {
  if (!props.showDate || !props.performance.date) return "";

  return formatDateTitle(props.performance.date);
});

const timeDisplay = computed(() => {
  if (props.performance.unixdatetime) {
    return unixDateTimeToTime(props.performance.unixdatetime);
  }

  // Fallback to time property if unixdatetime is not available
  if (props.performance.time) {
    return props.performance.time;
  }

  return "";
});

const attributes = computed(() => {
  return props.performance.performanceAuditoriumAttributeTitle || [];
});

const originalReleases = computed(() => {
  return props.performance.originalReleases;
});

const hasAttributes = computed(() => {
  return attributes.value.length > 0 || originalReleases.value;
});

const isDisabled = computed(() => {
  const perf = props.performance;
  // Button is disabled if sold out or not bookable, or if there's no booking link
  return !!perf.isSoldOut || !!perf.isNotBookable || !perf.bookingLink;
});

const disabledReason = computed(() => {
  const perf = props.performance;
  if (perf.isSoldOut) return "Vorstellung ausverkauft";
  if (perf.isNotBookable) return "Vorstellung nicht bucherbar";
  if (!perf.bookingLink) return "Kein Buchungslink verfügbar";
  return null;
});
</script>
